import React from 'react';
import { ArrowLeft, Copy, Save, Check, Trash2, Hammer } from 'lucide-react';
import type { OnePager } from '../types';
import { Editor } from '../components/NotionEditor';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/ApiService';
import { htmlToMarkdown } from '../lib/markdown-utils';

interface OnePagerViewProps {
    onePager: OnePager | null;
    onCreateFromTemplate: (onePager: OnePager) => void;
    onBack: () => void;
    onDelete?: (onePagerId: string) => void;
    loading?: boolean;
}

export const OnePagerView: React.FC<OnePagerViewProps> = ({
    onePager,
    onCreateFromTemplate,
    onBack,
    onDelete,
    loading = false
}) => {
    const { user } = useAuth();
    const [content, setContent] = React.useState<string>('');
    const [saving, setSaving] = React.useState(false);
    const [lastSaved, setLastSaved] = React.useState<Date | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [deleting, setDeleting] = React.useState(false);
    const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    // Convert OnePager to rich text content with actual data
    const convertToEditorContent = (onePager: OnePager): string => {
        console.log('Converting onePager to content:', onePager);

        let html = `<h1>${onePager.title}</h1>`;
        html += `<p><em>${onePager.description}</em></p>`;

        // Add sections if they exist
        if (onePager.spec.sections && Array.isArray(onePager.spec.sections)) {
            console.log('Adding sections:', onePager.spec.sections);
            onePager.spec.sections.forEach((section: any) => {
                html += `<h2>${section.title}</h2>`;
                html += `<p>${section.content}</p>`;
            });
        } else {
            console.log('No sections found, adding default content');
            html += `<h2>Overview</h2>`;
            html += `<p>Edit this section to add your content. Use the toolbar above to format your text with <strong>bold</strong>, <em>italic</em>, lists, and more.</p>`;
        }

        // Add technologies if they exist
        if (onePager.spec.technologies && Array.isArray(onePager.spec.technologies)) {
            console.log('Adding technologies:', onePager.spec.technologies);
            html += `<h2>Technologies</h2>`;
            html += `<ul>`;
            onePager.spec.technologies.forEach((tech: string) => {
                html += `<li>${tech}</li>`;
            });
            html += `</ul>`;
        }

        // Add priority if it exists
        if (onePager.spec.priority) {
            console.log('Adding priority:', onePager.spec.priority);
            html += `<blockquote><p>Priority: ${onePager.spec.priority}</p></blockquote>`;
        }

        console.log('Generated HTML content:', html);
        return html;
    };

    React.useEffect(() => {
        if (onePager) {
            // For editing, prefer the content field if it exists, otherwise convert from spec
            const initialContent = onePager.content || convertToEditorContent(onePager);
            console.log('OnePager data:', onePager);
            console.log('Initial content:', initialContent);
            setContent(initialContent);
        } else {
            // Clear content when no onePager
            setContent('');
        }
    }, [onePager]);

    // Check if user can edit this document
    const canEdit = user && onePager && (user.id === onePager.creator_id?.toString() || user.id === onePager.authorId);

    // Format relative time
    const getRelativeTime = (date: Date): string => {
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) {
            return `${diffInSeconds} seconds ago`;
        } else if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return minutes === 1 ? '1 min ago' : `${minutes} mins ago`;
        } else if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
        } else {
            const days = Math.floor(diffInSeconds / 86400);
            return days === 1 ? '1 day ago' : `${days} days ago`;
        }
    };

    // Auto-save functionality (only for editable documents)
    const saveContent = React.useCallback(async (contentToSave: string) => {
        if (!onePager || !user || !canEdit) return;

        try {
            setSaving(true);
            await apiService.updateOnePager(parseInt(onePager.id), {
                title: onePager.title,
                description: onePager.description,
                content: contentToSave,
                spec: onePager.spec,
                is_template: onePager.is_template
            });
            setLastSaved(new Date());
        } catch (error) {
            console.error('Failed to save:', error);
        } finally {
            setSaving(false);
        }
    }, [onePager, user, canEdit]);

    const handleContentChange = React.useCallback((newContent: string) => {
        if (!canEdit) return; // Prevent changes if user can't edit

        setContent(newContent);

        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Set new timeout for auto-save (2 seconds after user stops typing)
        saveTimeoutRef.current = setTimeout(() => {
            saveContent(newContent);
        }, 2000);
    }, [saveContent, canEdit]);

    const handleDelete = React.useCallback(async () => {
        if (!onePager || !canEdit) return;

        try {
            setDeleting(true);
            await apiService.deleteOnePager(parseInt(onePager.id));
            
            // Call the onDelete callback if provided
            if (onDelete) {
                onDelete(onePager.id);
            }
            
            // Navigate back to the list
            onBack();
        } catch (error) {
            console.error('Failed to delete one-pager:', error);
            alert('Failed to delete the one-pager. Please try again.');
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    }, [onePager, canEdit, onDelete, onBack]);

    if (!onePager) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <div className="text-gray-600 mb-4">One-pager not found.</div>
                    <button
                        onClick={onBack}
                        className="inline-flex items-center px-4 py-2 border border-gray-200 rounded-md text-sm text-gray-600 hover:text-gray-900 hover:border-gray-300"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            {/* Header */}
            <div className="sticky top-0 z-20 bg-white">
                <div className="max-w-6xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={onBack}
                                className="inline-flex items-center px-4 py-2 border border-gray-200 rounded-md text-sm text-gray-600 hover:text-gray-900 hover:border-gray-300"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </button>

                            {/* Edit status indicator */}
                            {!canEdit && (
                                <div className="hidden sm:inline-flex text-sm text-gray-600 bg-gray-50 px-2 py-1 rounded">
                                    View-only
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2 items-center">
                            {canEdit && saving && (
                                <div className="flex items-center text-sm text-gray-500">
                                    <Save className="w-4 h-4 mr-1 animate-spin" />
                                    Saving...
                                </div>
                            )}
                            {canEdit && lastSaved && !saving && (
                                <div className="hidden sm:flex items-center text-sm text-gray-600">
                                    <Check className="w-4 h-4 mr-1" />
                                    Saved <span className="hidden sm:inline">{getRelativeTime(lastSaved)}</span>
                                </div>
                            )}
                            {/* Build button */}
                            <button
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!onePager) return;
                                    try {
                                        await apiService.incrementBuildCount(parseInt(onePager.id));
                                    } catch (error) {
                                        console.error('Failed to track build:', error);
                                    }
                                    const markdownContent = htmlToMarkdown(onePager.content || '');
                                    const fullPrompt = `${onePager.title || ''}\n\n${onePager.description || ''}\n\n${markdownContent}`;
                                    const lovableUrl = `https://lovable.dev/?autosubmit=true#prompt=${encodeURIComponent(fullPrompt)}`;
                                    window.open(lovableUrl, '_blank');
                                }}
                                className="relative inline-flex items-center px-3 py-1.5 text-sm text-gray-900 bg-white rounded-md transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md before:absolute before:inset-0 before:rounded-md before:p-[2px] before:bg-gradient-to-r before:from-purple-500 before:via-blue-500 before:to-cyan-500 hover:before:from-purple-600 hover:before:via-blue-600 hover:before:to-cyan-600 before:-z-10 after:absolute after:inset-[2px] after:rounded-[calc(0.375rem-2px)] after:bg-white after:-z-10"
                            >
                                <Hammer className="w-4 h-4 mr-1" />
                                Build
                            </button>
                            {/* Clone button */}
                            <button
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!onePager) return;
                                    try {
                                        await apiService.incrementCloneCount(parseInt(onePager.id));
                                    } catch (error) {
                                        console.error('Failed to track clone:', error);
                                    }
                                    onCreateFromTemplate(onePager);
                                }}
                                className="inline-flex items-center px-3 py-1.5 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transform hover:scale-105 transition-all duration-200 shadow-sm hover:shadow-md"
                            >
                                <Copy className="w-4 h-4 mr-1" />
                                Clone
                            </button>
                            {/* Delete button - only show for editors */}
                            {canEdit && (
                                <div className="relative">
                                    <button
                                        onClick={() => setShowDeleteConfirm(true)}
                                        disabled={deleting}
                                        className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <Trash2 className="w-4 h-4 mr-1" />
                                        {deleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                    {/* Delete confirmation modal */}
                                    {showDeleteConfirm && (
                                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                                            <div className="bg-white rounded-lg p-6 max-w-md mx-4">
                                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                                    Delete One-Pager
                                                </h3>
                                                <p className="text-gray-600 mb-4">
                                                    Are you sure you want to delete "{onePager.title}"? This action cannot be undone.
                                                </p>
                                                <div className="flex gap-3 justify-end">
                                                    <button
                                                        onClick={() => setShowDeleteConfirm(false)}
                                                        className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50"
                                                        disabled={deleting}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleDelete}
                                                        disabled={deleting}
                                                        className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {deleting ? 'Deleting...' : 'Delete'}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Display/Editor */}
            <div className="max-w-6xl mx-auto px-6 pb-8">
                {/* Author and Last Edited */}
                <div className="flex flex-row items-center mb-2">
                    <div className="text-md font-semibold text-gray-400">
                        {onePager.creator?.fullName || onePager.creator?.username || 'Unknown'}
                    </div>
                    <div className="text-sm font-semibold text-gray-400">
                        {onePager.updated_at ? `, ${new Date(onePager.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).replace(/,/g, '')}` : 'Unknown'}
                    </div>
                </div>
                <Editor
                    content={content}
                    onUpdate={canEdit ? handleContentChange : undefined}
                    placeholder={canEdit ? "Start writing your document..." : "No content available"}
                    readOnly={!canEdit}
                />
            </div>
        </div>
    );
};